import { useEffect, useState } from 'react';
import { Card, Empty, ErrorBanner } from '../components/Layout.jsx';
import { apiFetch } from '../store.js';

// Approvals queue. The only WRITE this dashboard ever performs.

export default function TeamApprovals() {
  const [operators, setOperators] = useState([]);
  const [pending, setPending] = useState([]);
  const [reviewerId, setReviewerId] = useState('');
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function refresh() {
    setError(null);
    try {
      const [ops, list] = await Promise.all([
        apiFetch('/team/operators'),
        // The worker has no `/team/approvals/pending` endpoint in v1 — the
        // local backend owns approval queue persistence. The dashboard
        // exposes a writeback (POST /team/approve) only. Until /team/pending
        // ships, this list stays empty unless the caller's worker has been
        // extended.
        Promise.resolve({ operators: [], requests: [] }),
      ]);
      setOperators(ops.operators || []);
      setPending(list.requests || []);
    } catch (e) {
      setError(e);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function decide(requestId, approved) {
    if (!reviewerId) {
      setError(new Error('pick a reviewer first'));
      return;
    }
    setBusyId(requestId);
    setError(null);
    try {
      await apiFetch('/team/approve', {
        method: 'POST',
        body: {
          request_id: requestId,
          reviewer_id: reviewerId,
          approved,
          reason: approved ? '' : 'rejected via dashboard',
        },
      });
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <ErrorBanner error={error} />

      <Card title="Reviewer">
        <label style={{ display: 'block' }}>
          Sign-in as
          <select
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            style={{ display: 'block', width: '100%', marginTop: 4 }}
          >
            <option value="">(choose operator)</option>
            {operators.filter((o) => o.active && o.role === 'admin').map((o) => (
              <option key={o.operator_id} value={o.operator_id}>
                {o.display_name || o.email} — {o.role}
              </option>
            ))}
          </select>
        </label>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--type-utility)', marginTop: 8 }}>
          Only admins can approve. Decisions are written to the audit log
          under this reviewer's id.
        </p>
      </Card>

      <Card title={`Pending approvals (${pending.length})`}>
        {pending.length === 0 ? <Empty>No pending requests.</Empty> : (
          <table>
            <thead>
              <tr>
                <th>Request</th>
                <th>Reply</th>
                <th>Drafted by</th>
                <th>Reason</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.id}>
                  <td><code>{r.id?.slice(0, 8)}…</code></td>
                  <td>{r.reply_id}</td>
                  <td>
                    {operators.find((o) => o.operator_id === r.operator_id)?.display_name
                      || r.operator_id}
                  </td>
                  <td>{r.reason}</td>
                  <td>{r.created_at}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="btn"
                      disabled={!reviewerId || busyId === r.id}
                      onClick={() => decide(r.id, true)}
                    >
                      Approve
                    </button>
                    <button
                      className="btn danger"
                      disabled={!reviewerId || busyId === r.id}
                      onClick={() => decide(r.id, false)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Operators">
        <table>
          <thead>
            <tr>
              <th>Email</th><th>Display name</th><th>Role</th>
              <th>Brands</th><th>Active</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((o) => (
              <tr key={o.operator_id}>
                <td>{o.email}</td>
                <td>{o.display_name || '—'}</td>
                <td>{o.role}</td>
                <td>{(parseBrands(o.brands_allowed) || []).join(', ') || 'all'}</td>
                <td>{o.active ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function parseBrands(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
